// Notificaciones efímeras (toasts) para feedback de acciones.
import type { JSX } from "react";
import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import { create } from "zustand";

export type ToastKind = "success" | "error" | "info";
interface Toast { id: number; kind: ToastKind; message: string; }

interface ToastState {
  toasts: Toast[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: number) => void;
}

let seq = 1;
const DURATION = 4000;

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (kind, message) => {
    const id = seq++;
    set((s) => ({ toasts: [...s.toasts, { id, kind, message }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), DURATION);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Helpers imperativos para usar fuera de componentes. */
export const toast = {
  success: (m: string): void => useToasts.getState().push("success", m),
  error: (m: string): void => useToasts.getState().push("error", m),
  info: (m: string): void => useToasts.getState().push("info", m),
};

const CFG: Record<ToastKind, { icon: typeof Info; color: string; bar: string }> = {
  success: { icon: CheckCircle2, color: "text-success", bar: "bg-success" },
  error: { icon: XCircle, color: "text-danger", bar: "bg-danger" },
  info: { icon: Info, color: "text-primary", bar: "bg-primary" },
};

export function Toaster(): JSX.Element {
  const { toasts, dismiss } = useToasts();
  return (
    <div className="fixed top-4 right-4 z-[90] flex flex-col gap-2 w-80">
      {toasts.map((t) => {
        const c = CFG[t.kind]; const Icon = c.icon;
        return (
          <div
            key={t.id}
            className="relative card overflow-hidden flex items-start gap-2.5 pl-3.5 pr-2 py-3 cursor-pointer"
            style={{ animation: "toast-in 250ms var(--ease-out)", boxShadow: "var(--shadow-lg)" }}
            onClick={() => dismiss(t.id)}
            role="status"
          >
            <Icon size={18} strokeWidth={2} className={`${c.color} shrink-0 mt-0.5`} />
            <span className="flex-1 text-sm text-ink">{t.message}</span>
            <button className="text-ink-muted hover:text-ink-secondary shrink-0" aria-label="Cerrar"><X size={15} strokeWidth={2} /></button>
            <span className={`absolute bottom-0 left-0 h-0.5 ${c.bar}`} style={{ animation: `bar-drain ${DURATION}ms linear forwards` }} />
          </div>
        );
      })}
    </div>
  );
}
